import React from 'react';
import {
  getCategoriesSorted,
  getSubcategories,
} from '../../../data/categories';
import { getCategoryFields } from '../../../data/categoryFields';
import '../Create.css';
import ImageUploader from '../ImageUploader';
import LocationSelector from '../LocationSelector';
import CategorySection from './CategorySection';
import DeliverySection from './DeliverySection';
import DetailsSection from './DetailsSection';
import { FormSection } from './FormSection';
import PricingSection from './PricingSection';

// --- ICONS ---
const PAGE_HEADER_ICON = (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 5v14M5 12h14" />
  </svg>
);
const PHOTOS_ICON = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <polyline points="21 15 16 10 5 21" />
  </svg>
);
const BANK_ICON = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 22h18M6 18V11M10 18V11M14 18V11M18 18V11M12 2L2 7h20L12 2z" />
  </svg>
);
const LOCATION_ICON = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);
const CHECK_ICON = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const SAVE_ICON = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
    <polyline points="17 21 17 13 7 13 7 21" />
    <polyline points="7 3 7 8 15 8" />
  </svg>
);
const SPINNER_ICON = (
  <svg className="cf-btn-spinner" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="50 20" />
  </svg>
);

function AdForm({
  values,
  onChange,
  errors = {},
  loading = false,
  draftLoading = false,
  onSubmit,
  submitLabel,
  onSaveDraft,
}) {
  const anyLoading = loading || draftLoading;
  const categoriesList = getCategoriesSorted();
  const subcategoriesList = getSubcategories(values.category);
  const categoryFields = getCategoryFields(values.category);
  const extra = values.extra || {};
  const categoryHasListedBy = categoryFields.some((f) => f.key === 'listedBy');

  const payoutMethod = values.payoutMethod || { type: 'upi', upiId: '' };

  const handleChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    onChange(field, value);
  };

  const handleExtraChange = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    onChange('extra', { ...extra, [key]: value });
  };

  const handlePayoutChannelChange = (type) => {
    if (type === 'upi') {
      onChange('payoutMethod', { type: 'upi', upiId: '' });
    } else {
      onChange('payoutMethod', {
        type: 'bank',
        accountHolderName: '',
        accountNumber: '',
        ifscCode: '',
      });
    }
  };

  const handlePayoutFieldChange = (field, value) => {
    onChange('payoutMethod', {
      ...payoutMethod,
      [field]: value,
    });
  };

  return (
    <div className="cf-card">
      <div className="cf-page-header">
        <div className="cf-page-header-icon">{PAGE_HEADER_ICON}</div>
        <div>
          <h2 className="cf-page-title">Post your ad</h2>
          <p className="cf-page-subtitle">
            Fill in the details below to list your item
          </p>
        </div>
      </div>

      <CategorySection
        values={values}
        onChange={onChange}
        errors={errors}
        categoriesList={categoriesList}
        subcategoriesList={subcategoriesList}
        categoryFields={categoryFields}
        handleChange={handleChange}
        handleExtraChange={handleExtraChange}
        extra={extra}
      />
      <DetailsSection
        values={values}
        onChange={onChange}
        errors={errors}
        handleChange={handleChange}
      />
      <PricingSection
        values={values}
        onChange={onChange}
        errors={errors}
        handleChange={handleChange}
        categoryHasListedBy={categoryHasListedBy}
      />

      {/* --- MEDIA SECTION --- */}
      <FormSection
        icon={PHOTOS_ICON}
        title="Photos & Media"
        subtitle="Great photos attract more buyers"
      >
        <div className="cf-field">
          <ImageUploader
            images={values.images}
            onChange={(v) => onChange('images', v)}
          />
          {errors.images && <span className="cf-error">{errors.images}</span>}
        </div>
        <div className="cf-field">
          <label htmlFor="ad-videoUrl">
            Video link <span className="cf-optional">optional</span>
          </label>
          <input
            id="ad-videoUrl"
            className="cf-input"
            type="url"
            placeholder="Paste a YouTube or video URL"
            value={values.videoUrl || ''}
            onChange={handleChange('videoUrl')}
          />
        </div>
      </FormSection>

      {/* --- 🏦 VENDOR PAYOUT CHANNELS MANAGEMENT SECTION --- */}
      <FormSection
        icon={BANK_ICON}
        title="Payout Settlement Profile"
        subtitle="Where should the admin disburse your 90% split after delivery clearance?"
      >
        <div className="cf-field" style={{ marginBottom: '18px' }}>
          <label style={{ fontWeight: '500', marginBottom: '8px', display: 'block' }}>Preferred Settlement Method</label>
          <div style={{ display: 'flex', gap: '24px', marginTop: '4px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
              <input
                type="radio"
                name="payoutChannel"
                checked={payoutMethod.type === 'upi'}
                onChange={() => handlePayoutChannelChange('upi')}
              />
              UPI ID
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
              <input
                type="radio"
                name="payoutChannel"
                checked={payoutMethod.type === 'bank'}
                onChange={() => handlePayoutChannelChange('bank')}
              />
              Direct National Bank Transfer
            </label>
          </div>
        </div>

        {payoutMethod.type === 'upi' ? (
          <div className="cf-field">
            <label htmlFor="ad-upiId">UPI Address Target</label>
            <input
              id="ad-upiId"
              className="cf-input"
              type="text"
              placeholder="e.g., yourname@oksbi"
              value={payoutMethod.upiId || ''}
              onChange={(e) => handlePayoutFieldChange('upiId', e.target.value)}
              required
            />
            {errors.payoutMethod?.upiId && <span className="cf-error">{errors.payoutMethod.upiId}</span>}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div className="cf-field">
              <label>Account Holder Name</label>
              <input
                type="text"
                className="cf-input"
                placeholder="Exactly as registered on bank identity passbook"
                value={payoutMethod.accountHolderName || ''}
                onChange={(e) => handlePayoutFieldChange('accountHolderName', e.target.value)}
                required
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div className="cf-field">
                <label>Account Number</label>
                <input
                  type="text"
                  className="cf-input"
                  placeholder="Enter ledger account index string"
                  value={payoutMethod.accountNumber || ''}
                  onChange={(e) => handlePayoutFieldChange('accountNumber', e.target.value)}
                  required
                />
              </div>
              <div className="cf-field">
                <label>IFSC Routing Code</label>
                <input
                  type="text"
                  className="cf-input"
                  placeholder="e.g., SBIN0001234"
                  value={payoutMethod.ifscCode || ''}
                  onChange={(e) => handlePayoutFieldChange('ifscCode', e.target.value.toUpperCase())}
                  required
                />
              </div>
            </div>
            {errors.payoutMethod && <span className="cf-error">{errors.payoutMethod}</span>}
          </div>
        )}
      </FormSection>

      {/* --- 📍 LOCATION & COURIER PICKUP MANAGEMENT SECTION --- */}
      <FormSection
        icon={LOCATION_ICON}
        title="Location & Pickup Address"
        subtitle="Help buyers find items near them and specify where couriers should collect the package."
      >
        <div className="cf-field" style={{ marginBottom: '16px' }}>
          <label style={{ fontWeight: '500' }}>General Location (City/Area)</label>
          <LocationSelector
            location={values.location}
            onChange={(v) => onChange('location', v)}
            error={errors.location}
          />
        </div>

        {/* 🚀 NEW FIELD: SELLER MOBILE PHONE CAPTURE */}
        <div className="cf-field" style={{ marginBottom: '16px' }}>
          <label htmlFor="ad-sellerPhone" style={{ fontWeight: '500' }}>
            Pickup Contact Mobile Number <span style={{ color: '#e63946' }}>*</span>
          </label>
          <input
            id="ad-sellerPhone"
            className="cf-input"
            type="tel"
            pattern="[0-9]{10}"
            placeholder="10-digit mobile number for courier arrival coordinates"
            value={values.sellerPhone || ''}
            onChange={handleChange('sellerPhone')}
            required
          />
          {errors.sellerPhone && <span className="cf-error">{errors.sellerPhone}</span>}
          <small style={{ color: '#64748b', fontSize: '12px', marginTop: '4px', display: 'block' }}>

          </small>
        </div>

        <div className="cf-field">
          <label htmlFor="ad-pickupAddress" style={{ fontWeight: '500' }}>
            Full Physical Pickup Address <span style={{ color: '#e63946' }}>*</span>
          </label>
          <textarea
            id="ad-pickupAddress"
            className="cf-input"
            rows="3"
            placeholder="Flat/House No., Building, Street Name, Pincode. (Ensure this is accurate for the courier pickup)"
            value={values.pickupAddress || ''}
            onChange={handleChange('pickupAddress')}
            style={{ width: '100%', padding: '10px', borderRadius: '6px', resize: 'vertical', fontFamily: 'inherit' }}
            required
          />
          {errors.pickupAddress && <span className="cf-error">{errors.pickupAddress}</span>}
        </div>
      </FormSection>

      {/* --- SUBMIT ACTIONS --- */}
      <div className="cf-actions">
        <button
          className={`cf-btn cf-btn--primary${loading ? ' cf-btn--loading' : ''}`}
          type="button"
          onClick={onSubmit}
          disabled={anyLoading}
        >
          {loading ? SPINNER_ICON : CHECK_ICON}
          {loading ? 'Posting...' : submitLabel}
        </button>
        {onSaveDraft && (
          <button
            className={`cf-btn cf-btn--secondary${draftLoading ? ' cf-btn--loading' : ''}`}
            type="button"
            onClick={onSaveDraft}
            disabled={anyLoading}
          >
            {draftLoading ? SPINNER_ICON : SAVE_ICON}
            {draftLoading ? 'Saving...' : 'Save as draft'}
          </button>
        )}
      </div>
    </div>
  );
}

export default AdForm;
